import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/baseUrl';
import Loading from '../components/Loading';

// YYYY-MM-DD (native date input) -> DD/MM/YYYY (API format)
const toApiDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};
const inputDate = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const humanize = (k) =>
  String(k)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
const fmt = (v) => (typeof v === 'number' ? v.toFixed(2) : v ?? '-');

const CompanyReportDatePage = () => {
  const [fromDate, setFromDate] = useState(inputDate(-7));
  const [toDate, setToDate] = useState(inputDate(0));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No access token found');
        setLoading(false);
        return;
      }
      const baseUrl = localStorage.getItem('custom_base_url') || API_BASE_URL;
      const res = await axios.get(`${baseUrl}/api/company-report-date`, {
        params: { from: toApiDate(fromDate), to: toApiDate(toDate) },
        headers: { Authorization: 'Bearer ' + token },
      });
      const d = res.data;
      if (d?.success === false) {
        setError(d.message || 'Failed to load report');
        setRows([]);
        setSummary(null);
      } else {
        const list = Array.isArray(d) ? d : d?.data ?? d?.report ?? d?.rows ?? [];
        setRows(Array.isArray(list) ? list : []);
        setSummary(d?.summary ?? d?.totals ?? null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report');
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = rows.length
    ? Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== 'object')
    : [];

  return (
    <div className="w-full">
      <div className="w-full px-2">
        <div className="bg-white rounded shadow border border-gray-200">
          {/* Header */}
          <div className="bg-gray-600 px-5 py-3">
            <span className="text-xl text-white font-normal">Company Report</span>
          </div>

          {/* Date filters */}
          <div className="flex flex-wrap items-end gap-3 px-5 py-3">
            <div>
              <label className="block text-xs mb-1 text-gray-500">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs mb-1 text-gray-500">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <button
              onClick={fetchReport}
              disabled={loading}
              className="bg-blue-500 text-white px-5 py-2 rounded hover:bg-blue-600 disabled:opacity-60"
            >
              {loading ? 'Loading…' : 'Get Report'}
            </button>
          </div>

          {/* Summary */}
          {summary && typeof summary === 'object' && (
            <div className="flex flex-wrap gap-3 px-5 pb-3">
              {Object.entries(summary)
                .filter(([, v]) => typeof v !== 'object')
                .map(([k, v]) => (
                  <div key={k} className="px-3 py-2 rounded bg-gray-50 border border-gray-200 text-sm">
                    <span className="text-gray-500">{humanize(k)}: </span>
                    <span
                      className={`font-semibold ${
                        typeof v === 'number' ? (v >= 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-800'
                      }`}
                    >
                      {fmt(v)}
                    </span>
                  </div>
                ))}
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto px-2 pb-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loading />
              </div>
            ) : error ? (
              <div className="p-4 text-red-500 text-center">{error}</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No data for the selected dates</div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#0F2327] text-white">
                    {columns.map((c) => (
                      <th key={c} className="p-2 border border-gray-600 text-left whitespace-nowrap">
                        {humanize(c)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {columns.map((c) => {
                        const v = row[c];
                        const num = typeof v === 'number';
                        return (
                          <td
                            key={c}
                            className={`p-2 border border-gray-300 whitespace-nowrap ${
                              num ? (v >= 0 ? 'text-green-600' : 'text-red-600') : ''
                            }`}
                          >
                            {fmt(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyReportDatePage;
